import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  OnChanges,
  Output,
  SimpleChanges
} from '@angular/core';
import { DropdownBaseQueryInterface } from '@zaoblako/shared/common/dtos/dropdown-base-query.dto';
import { DEFAULT_LIMIT, DEFAULT_OFFSET } from '@zaoblako/shared/common/dtos/pagination-query.dto';
import { PaginationFindInterface } from '@zaoblako/shared/common/dtos/pagination-find.dto';
import { USER_WIDGET_REGISTRY_SELECT } from '@zaoblako/shared/modules/security/dtos/user/user-registry-select.dto';
import type { UserFindInterface } from '@zaoblako/shared/modules/security/dtos/user/user-find.dto';
import { UserService } from '@zaoblako/web/core/services/security/user.service';
import { BaseSingleSelectComponent } from '@zaoblako/web/shared/components/base-single-select/base-single-select.component';
import { SingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/single-select/single-select.component';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import { SafeHtmlPipe } from '@zaoblako/web/shared/pipes/safe-html.pipe';
import { map, Observable } from 'rxjs';

@Component({
  selector: 'crm-user-single-select',
  standalone: true,
  imports: [CommonModule, SingleSelectComponent, SpinnerComponent, SafeHtmlPipe],
  templateUrl: './user-single-select.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UserSingleSelectComponent extends BaseSingleSelectComponent<UserFindInterface> implements OnInit, OnChanges {
  public availableModule = 'BASIS';

  @Input() public users: UserFindInterface[] = [];
  @Output() public selectedUserChange = new EventEmitter<UserFindInterface>();

  @Input() public set selectedUser(value: UserFindInterface) {
    this.selectedEntity = value;
  }

  @Input() public scrollHeight: string = '300px';
  @Input() public itemSubDescriptionField = 'job';
  @Input() public sort = 'profile.name:string';

  public constructor(private readonly userService: UserService) {
    super();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.users && this.customSearch) {
      this.entities = this.users ?? [];
      this.cdRef.markForCheck();
    }
    if (changes.users && !this.users?.length && !this.customSearch && !changes.users.firstChange) {
      this.firstLoad();
    }
    super.ngOnChanges(changes);
  }

  public findById(id: number): Observable<UserFindInterface> {
    return this.userService.findById(id, { withDeleted: true });
  }

  public findWithPagination(
    queryParams: Partial<DropdownBaseQueryInterface>
  ): Observable<PaginationFindInterface<UserFindInterface>> {
    return this.userService.findRegistryItems({
      ...queryParams,
      limit: queryParams?.limit ?? DEFAULT_LIMIT,
      offset: queryParams?.offset ?? DEFAULT_OFFSET,
      select: [...USER_WIDGET_REGISTRY_SELECT],
      withBanned: !!queryParams.includeIds?.length,
      withDeleted: !!queryParams.includeIds?.length
    }).pipe(
      map(({ items, count, limit, offset }) => ({ items, count, limit, offset }))
    );
  }

  public onSelect(user: UserFindInterface): void {
    this.selectedUserChange.emit(user);
    super.onSelect(user);
  }

  public toUser(user: unknown): UserFindInterface {
    return user as UserFindInterface;
  }
}
